import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FadeInImage from '../../shared/components/FadeInImage';

/**
 * Curated Moroccan destinations. Each card links to /events?city=<Name>
 * (the events listing accepts a `city` filter). Images are picked from
 * /storage/events/ assets we already ship.
 *
 * Desktop: bento-style asymmetric grid (1 hero tile + 5 smaller).
 * Mobile : horizontal snap-scroll strip.
 */
const DESTINATIONS = [
  {
    name: 'Marrakech',
    tagline: 'La ville rouge',
    img: '/storage/events/air-desert-a-marrakech-events-partner-648-2024-05-21.jpeg',
    hero: true,
  },
  {
    name: 'Chefchaouen',
    tagline: 'La perle bleue',
    img: '/storage/events/chefchaouen-floatation-center-events-partner-638-2024-04-30.jpeg',
  },
  {
    name: 'Essaouira',
    tagline: 'Vent & océan',
    img: '/storage/events/balade-a-cheval-essaouira-events-1-2021-03-12.jpeg',
  },
  {
    name: 'Agadir',
    tagline: 'Soleil & plages',
    img: '/storage/events/balade-a-cheval-agadir-events-partner-240-2021-11-26.jpg',
  },
  {
    name: 'Fès',
    tagline: 'Capitale spirituelle',
    img: '/storage/events/atelier-poterie-et-ceramique-a-fes-events-partner-244-2021-11-27.jpg',
  },
  {
    name: 'Tanger',
    tagline: 'Porte du détroit',
    img: '/storage/events/activite-de-buggy-a-tanger-events-partner-657-2024-06-07.jpeg',
  },
];

function DestinationTile({ city, hero, fixedSize }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/events?city=${encodeURIComponent(city.name)}`}
      className={`group relative block overflow-hidden rounded-2xl shadow-card transition-all duration-300 ease-out-quint hover:-translate-y-1 hover:shadow-elevated ${
        hero ? 'lg:row-span-2 lg:col-span-2' : ''
      } ${fixedSize ? 'shrink-0' : ''}`}
      style={fixedSize ? { width: '260px', height: '320px' } : undefined}
    >
      <div className={`relative h-full w-full ${hero && !fixedSize ? 'min-h-[500px] lg:min-h-0' : !fixedSize ? 'min-h-[240px]' : ''} overflow-hidden`}>
        <div className="h-full w-full transition-transform duration-[900ms] ease-out-quint group-hover:scale-[1.08]">
          <FadeInImage
            src={city.img}
            alt={city.name}
            className="h-full w-full object-cover"
          />
        </div>
        {/* gradient for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

        {/* content overlay */}
        <div className="absolute inset-x-0 bottom-0 p-5">
          <p className="text-xs font-medium uppercase tracking-widest text-accent-500 drop-shadow">
            {city.tagline}
          </p>
          <h3
            className={`font-display font-semibold leading-tight text-white drop-shadow ${
              hero ? 'mt-1 text-3xl sm:text-4xl' : 'mt-0.5 text-xl sm:text-2xl'
            }`}
          >
            {city.name}
          </h3>

          {/* explore link with arrow */}
          <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white">
            <span className="border-b border-white/40 pb-0.5 transition-colors group-hover:border-accent-500 group-hover:text-accent-500">
              {t('home.citiesExploreCta', 'Explore')}
            </span>
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 transition-all duration-300 group-hover:translate-x-1 group-hover:text-accent-500 rtl:rotate-180 rtl:group-hover:-translate-x-1" aria-hidden>
              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Destinations() {
  const { t } = useTranslation();
  const hero = DESTINATIONS[0];
  const rest = DESTINATIONS.slice(1);

  return (
    <section className="relative bg-surface-muted py-12 sm:py-16">
      <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-500">
            {t('home.citiesEyebrow')}
          </span>
          <h2 className="mt-3 font-display text-[19px] font-semibold tracking-tight text-ink sm:text-[28px] lg:text-3xl">
            {t('home.citiesTitle')}
          </h2>
          <p className="mt-1.5 hidden text-sm text-ink-muted sm:block">
            {t('home.citiesSubtitle')}
          </p>
        </div>

        {/* Desktop: editorial bento grid (hero 2x2 + 4 smaller in 2 rows) */}
        <div className="mt-8 hidden h-[480px] lg:grid lg:grid-cols-4 lg:grid-rows-2 lg:gap-4">
          <DestinationTile city={hero} hero />
          {rest.slice(0, 4).map((c) => (
            <DestinationTile key={c.name} city={c} />
          ))}
        </div>

        {/* Tablet/mobile: horizontal snap-scroll strip — fixed-size tiles */}
        <div className="mt-6 lg:hidden">
          <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {DESTINATIONS.map((c) => (
              <DestinationTile key={c.name} city={c} fixedSize />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
